import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../core/context/AuthContext";
import {
  ACTION_ITEM_STATUS_PENDING,
  buildActionItemsListPath,
  isBusinessActionItemsHomePath,
  isStarredFilterActiveFromSearch,
} from "./actionItemHelpers";
import { useActionItemsSidebarOptional } from "./ActionItemsSidebarContext";
import { renderWorkflowSourceIcon } from "./actionItemSourceIcons";
import {
  ActionItemStarFilledIcon,
  ActionItemStarIcon,
} from "./shared/actionItemStarIcons";
import {
  addPinnedFilter,
  buildPinnedFilterOptions,
  buildPinnedFilterPath,
  isPinnedFilterActive,
  readPinnedFilters,
  removePinnedFilter,
  writePinnedFilters,
} from "./shared/pinnedFilters";

const PICKER_GROUPS = [
  { kind: "source", title: "Source" },
  { kind: "category", title: "Category" },
  { kind: "assist", title: "AI Actions" },
];

const PinnedFilterIcon = ({ entry }) =>
  entry.kind === "source" && entry.workflowType ? (
    renderWorkflowSourceIcon(entry.workflowType)
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M4 5h16l-6.2 7.4v5.1l-3.6 2.1v-7.2L4 5z" strokeLinejoin="round" />
    </svg>
  );

/**
 * Sidebar shortcuts to the task list: Starred, plus any filters the user pinned.
 * "Add filter" opens a small picker of the existing filters; nothing is pinned by default.
 *
 * @example
 * <BusinessSidebarNav items={[{ status: "pending", pinned: true }]} />
 * // "Starred 1" row, then "+ Add filter"; click Starred → /business/action-items?starred=1
 */
const BusinessSidebarNav = ({ items = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || user?.email || "";
  const sidebar = useActionItemsSidebarOptional();
  const onTaskList = isBusinessActionItemsHomePath(location.pathname);
  const starredActive = onTaskList && isStarredFilterActiveFromSearch(location.search);
  const starredCount = (Array.isArray(items) ? items : []).filter(
    (item) => item?.status === ACTION_ITEM_STATUS_PENDING && Boolean(item?.pinned)
  ).length;

  const [pinned, setPinned] = useState(() => readPinnedFilters(userId));
  const [pickerOpen, setPickerOpen] = useState(false);
  // Two-step picker: pick a filter type first, then the value ("" = the type list).
  const [pickerKind, setPickerKind] = useState("");
  const pickerRef = useRef(null);

  useEffect(() => {
    setPinned(readPinnedFilters(userId));
  }, [userId]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onPointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setPickerOpen(false);
        setPickerKind("");
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setPickerOpen(false);
        setPickerKind("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  const options = useMemo(() => {
    const all = buildPinnedFilterOptions({
      workflowEntries: sidebar?.sourceNav?.workflowEntries,
      hasManualItems: sidebar?.sourceNav?.hasManualItems,
      categoryCatalog: sidebar?.categoryCatalog,
    });
    const isPinned = (option) =>
      pinned.some((entry) => entry.kind === option.kind && entry.id === option.id);
    return PICKER_GROUPS.map((group) => ({
      ...group,
      entries: all[group.kind].filter((option) => !isPinned(option)),
    })).filter((group) => group.entries.length > 0);
  }, [sidebar?.sourceNav, sidebar?.categoryCatalog, pinned]);

  const activeGroup = options.find((group) => group.kind === pickerKind) || null;

  const handleAdd = useCallback(
    (option) => {
      setPinned(writePinnedFilters(userId, addPinnedFilter(pinned, option)));
      setPickerOpen(false);
      setPickerKind("");
    },
    [pinned, userId]
  );

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPickerKind("");
  }, []);

  const handleRemove = useCallback(
    (entry) => {
      setPinned(writePinnedFilters(userId, removePinnedFilter(pinned, entry)));
    },
    [pinned, userId]
  );

  return (
    <nav className="business-sidebar-nav" aria-label="Task views">
      <button
        type="button"
        className={`business-sidebar-nav__item${
          starredActive ? " business-sidebar-nav__item--active" : ""
        }`}
        aria-current={starredActive ? "page" : undefined}
        onClick={() =>
          navigate(buildActionItemsListPath({ starred: !starredActive }))
        }
      >
        <span className="business-sidebar-nav__icon" aria-hidden="true">
          {starredActive ? <ActionItemStarFilledIcon size={15} /> : <ActionItemStarIcon size={15} />}
        </span>
        <span className="business-sidebar-nav__label">Starred</span>
        {starredCount > 0 ? (
          <span className="business-sidebar-nav__count">{starredCount}</span>
        ) : null}
      </button>
      {pinned.map((entry) => {
        const active = onTaskList && isPinnedFilterActive(entry, location.search);
        return (
          <div
            key={`${entry.kind}:${entry.id}`}
            className={`business-sidebar-nav__pinned${
              active ? " business-sidebar-nav__pinned--active" : ""
            }`}
          >
            <button
              type="button"
              className="business-sidebar-nav__item business-sidebar-nav__item--pinned"
              aria-current={active ? "page" : undefined}
              onClick={() =>
                navigate(active ? buildActionItemsListPath() : buildPinnedFilterPath(entry))
              }
            >
              <span className="business-sidebar-nav__icon" aria-hidden="true">
                <PinnedFilterIcon entry={entry} />
              </span>
              <span className="business-sidebar-nav__label" title={entry.label}>
                {/* Full name as the user kept it; a break opportunity after "@" keeps long emails readable. */}
                {entry.label.replace(/@/g, "@\u200b")}
              </span>
            </button>
            <button
              type="button"
              className="business-sidebar-nav__pinned-remove"
              aria-label={`Remove ${entry.label} filter`}
              title="Remove"
              onClick={() => handleRemove(entry)}
            >
              ×
            </button>
          </div>
        );
      })}
      {options.length > 0 || pickerOpen ? (
        <div className="business-sidebar-nav__add-wrap" ref={pickerRef}>
          <button
            type="button"
            className="business-sidebar-nav__add"
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            onClick={() => (pickerOpen ? closePicker() : setPickerOpen(true))}
          >
            <span aria-hidden="true">+</span> Add filter
          </button>
          {pickerOpen ? (
            <div className="business-sidebar-nav__picker" role="listbox" aria-label="Add a filter">
              {activeGroup ? (
                <>
                  <button
                    type="button"
                    className="business-sidebar-nav__picker-back"
                    onClick={() => setPickerKind("")}
                  >
                    <span aria-hidden="true">←</span> {activeGroup.title}
                  </button>
                  {activeGroup.entries.map((option) => (
                    <button
                      key={`${option.kind}:${option.id}`}
                      type="button"
                      role="option"
                      aria-selected="false"
                      className="business-sidebar-nav__picker-option"
                      onClick={() => handleAdd(option)}
                    >
                      {option.label}
                    </button>
                  ))}
                </>
              ) : (
                options.map((group) =>
                  group.kind === "assist" ? (
                    <button
                      key={group.kind}
                      type="button"
                      role="option"
                      aria-selected="false"
                      className="business-sidebar-nav__picker-option"
                      onClick={() => handleAdd(group.entries[0])}
                    >
                      {group.entries[0].label}
                    </button>
                  ) : (
                    <button
                      key={group.kind}
                      type="button"
                      role="option"
                      aria-selected="false"
                      className="business-sidebar-nav__picker-option business-sidebar-nav__picker-option--type"
                      onClick={() => setPickerKind(group.kind)}
                    >
                      <span>{group.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  )
                )
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </nav>
  );
};

export default BusinessSidebarNav;
